// EDEN · the worksheet/test designer's block model (Module P2) — one small registry entry per
// block type, each owning its own content shape, edit form, and screen/print rendering. New block
// types are added here only; test-editor.ts never special-cases a type by name.
//
// Every block structurally carries its own answer key (mcq's correctIndex, gap_fill's gaps,
// matching's key, short_answer/listening's key text) — unlike the Item Bank's free-text cards,
// nothing here needs the worksheet.py-style "guess where the answer space goes" furniture; each
// type already knows its own shape and draws its own blank space on the student sheet.

import { esc } from "./dom";

export type BlockType =
  | "instructions" | "reading_passage" | "mcq" | "gap_fill" | "matching"
  | "short_answer" | "writing" | "listening" | "picture" | "section" | "spelling";

export const BLOCK_TYPES: BlockType[] = [
  "section", "instructions", "reading_passage", "mcq", "gap_fill", "matching",
  "spelling", "short_answer", "writing", "listening", "picture",
];

export const BLOCK_LABEL: Record<BlockType, string> = {
  section: "Section heading",
  instructions: "Instructions",
  reading_passage: "Reading passage",
  mcq: "Multiple choice",
  gap_fill: "Gap fill",
  matching: "Matching",
  spelling: "Spelling",
  short_answer: "Short answer",
  writing: "Writing",
  listening: "Listening",
  picture: "Picture",
};

export interface EditorBlock {
  id: string;
  type: BlockType;
  marks: number;
  bloom: "k" | "c" | "a" | null;
  difficulty: "E" | "M" | "D" | null;
  content: Record<string, unknown>;
}

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const num = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);

/** Split a textarea's "one per line" input into trimmed, non-empty lines. */
const lines = (text: string): string[] => text.split("\n").map((l) => l.trim()).filter(Boolean);

// ── per-type defaults, edit form, and rendering ──────────────────────────────────────────────
interface BlockDef {
  newContent(): Record<string, unknown>;
  /** The edit form's inner HTML — inputs are named so `readForm` can read them back by name. */
  editForm(c: Record<string, unknown>): string;
  /** Read the edit form (already in the DOM) back into a content object. */
  readForm(form: HTMLElement): Record<string, unknown>;
  /** The student-facing view — screen AND print share this (blocks don't need a separate print
   *  layout the way the plan template does; a worksheet's screen preview IS the paper, just in a
   *  browser tab, per the print spec: "student copy first ... @media print only; no PDF library"). */
  renderStudent(c: Record<string, unknown>): string;
  /** Teacher-only: the answer key, shown in the editor and on the key page — never the student page. */
  renderKey(c: Record<string, unknown>): string;
  /** One line for the collapsed block card in the editor list. */
  summary(c: Record<string, unknown>): string;
}

const BLOCKS: Record<BlockType, BlockDef> = {
  // A section carries no marks/content of its own — its printed Roman/letter label and its
  // auto-summed marks are computed from the WHOLE block list by section-layout.ts, not from this
  // content object (see that file's header for why this is the one block type test-print.ts and
  // test-editor.ts DO special-case). newContent/editForm/readForm below cover only `title`+`level`.
  section: {
    newContent: () => ({ title: "", level: 1 }),
    editForm: (c) => `
      <label>Section title</label><input name="title" value="${esc(str(c.title))}" placeholder="Vocabulary" />
      <label>Level</label>
      <select name="level">
        <option value="1" ${num(c.level, 1) === 1 ? "selected" : ""}>Section (I, II, III…)</option>
        <option value="2" ${num(c.level, 1) === 2 ? "selected" : ""}>Sub-section (A, B, C…)</option>
      </select>
      <p class="hint">Marks print as the sum of every block that follows, up to the next section — never typed here.</p>`,
    readForm: (f) => ({
      title: (f.querySelector('[name="title"]') as HTMLInputElement).value.trim(),
      level: Number((f.querySelector('[name="level"]') as HTMLSelectElement | null)!.value) === 2 ? 2 : 1,
    }),
    renderStudent: () => "", // test-print.ts renders the section heading itself, with its computed label + marks
    renderKey: () => "",
    summary: (c) => `${num(c.level, 1) === 2 ? "Sub-section" : "Section"}: ${str(c.title) || "(untitled)"}`,
  },

  instructions: {
    newContent: () => ({ text: "" }),
    editForm: (c) => `<label>Instructions text</label><textarea name="text" placeholder="Read the passage, then answer the questions below.">${esc(str(c.text))}</textarea>`,
    readForm: (f) => ({ text: (f.querySelector('[name="text"]') as HTMLTextAreaElement).value.trim() }),
    renderStudent: (c) => (str(c.text) ? `<p class="blk-instr">${esc(str(c.text))}</p>` : `<p class="blk-instr hint">Add instructions text.</p>`),
    renderKey: () => "",
    summary: (c) => str(c.text).slice(0, 80) || "(empty)",
  },

  reading_passage: {
    newContent: () => ({ title: "", text: "" }),
    editForm: (c) => `
      <label>Title (optional)</label><input name="title" value="${esc(str(c.title))}" placeholder="The Lost Kite" />
      <label>Passage text</label><textarea name="text" style="min-height:160px">${esc(str(c.text))}</textarea>`,
    readForm: (f) => ({
      title: (f.querySelector('[name="title"]') as HTMLInputElement).value.trim(),
      text: (f.querySelector('[name="text"]') as HTMLTextAreaElement).value.trim(),
    }),
    renderStudent: (c) => {
      const title = str(c.title) ? `<p class="blk-passage-title">${esc(str(c.title))}</p>` : "";
      const body = str(c.text)
        .split("\n").map((p) => p.trim()).filter(Boolean)
        .map((p) => `<p>${esc(p)}</p>`).join("");
      return `<div class="blk-passage">${title}${body || `<p class="hint">Add the passage text.</p>`}</div>`;
    },
    renderKey: () => "",
    summary: (c) => str(c.title) || str(c.text).slice(0, 60) || "(empty)",
  },

  mcq: {
    newContent: () => ({ stem: "", options: ["", "", ""], correctIndex: null }),
    editForm: (c) => {
      const opts = arr(c.options).length ? arr(c.options) : ["", "", ""];
      const correct = typeof c.correctIndex === "number" ? c.correctIndex : null;
      return `
        <label>Question stem</label><textarea name="stem">${esc(str(c.stem))}</textarea>
        <label>Options (3–4) — tick the correct one</label>
        ${opts.map((o, i) => `
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:6px">
            <input type="radio" name="correct" value="${i}" ${correct === i ? "checked" : ""} aria-label="Correct option ${i + 1}" />
            <input name="opt${i}" value="${esc(o)}" placeholder="Option ${String.fromCharCode(65 + i)}" style="flex:1" />
          </div>`).join("")}
        <div class="row-actions"><a href="#" data-blk-act="add-opt" class="linkbtn">+ Add option</a></div>`;
    },
    readForm: (f) => {
      const opts: string[] = [];
      for (let i = 0; i < 4; i++) {
        const el = f.querySelector(`[name="opt${i}"]`) as HTMLInputElement | null;
        if (el) opts.push(el.value.trim());
      }
      const checked = f.querySelector('input[name="correct"]:checked') as HTMLInputElement | null;
      return { stem: (f.querySelector('[name="stem"]') as HTMLTextAreaElement).value.trim(), options: opts, correctIndex: checked ? Number(checked.value) : null };
    },
    renderStudent: (c) => {
      const opts = arr(c.options).filter(Boolean);
      // Short options ("true/false", single words) print on one line, as the paper does; longer
      // phrases each get their own line so they don't wrap into each other.
      const short = opts.length > 0 && opts.every((o) => o.length <= 14);
      const rows = opts.map((o, i) => `<label class="blk-mcq-opt"><input type="radio" disabled /> ${String.fromCharCode(65 + i)}) ${esc(o)}</label>`).join("");
      return `<div class="blk-mcq${short ? " blk-mcq-inline" : ""}"><p class="blk-stem">${esc(str(c.stem)) || `<span class="hint">Add the question.</span>`}</p>${rows}</div>`;
    },
    renderKey: (c) => {
      const i = typeof c.correctIndex === "number" ? c.correctIndex : null;
      const opts = arr(c.options);
      return i !== null && opts[i] ? `<strong>Answer:</strong> ${String.fromCharCode(65 + i)}. ${esc(opts[i]!)}` : `<span class="hint">No correct option set.</span>`;
    },
    summary: (c) => str(c.stem).slice(0, 80) || "(empty)",
  },

  gap_fill: {
    newContent: () => ({ text: "", gaps: [] }),
    editForm: (c) => `
      <label>Text (use ___ for each gap)</label>
      <textarea name="text" placeholder="The cat ___ on the mat.">${esc(str(c.text))}</textarea>
      <label>Answers, one per line, in gap order</label>
      <textarea name="gaps">${esc(arr(c.gaps).join("\n"))}</textarea>`,
    readForm: (f) => ({
      text: (f.querySelector('[name="text"]') as HTMLTextAreaElement).value,
      gaps: lines((f.querySelector('[name="gaps"]') as HTMLTextAreaElement).value),
    }),
    renderStudent: (c) => {
      const text = str(c.text);
      if (!text) return `<p class="hint">Add the gapped text.</p>`;
      let n = 0;
      const html = esc(text).replace(/_{3,}/g, () => `<span class="blk-gap">(${++n})&nbsp;______</span>`);
      return `<p class="blk-gapfill">${html}</p>`;
    },
    renderKey: (c) => (arr(c.gaps).length ? arr(c.gaps).map((g, i) => `(${i + 1}) ${esc(g)}`).join(" · ") : `<span class="hint">No answers listed.</span>`),
    summary: (c) => str(c.text).slice(0, 80) || "(empty)",
  },

  matching: {
    newContent: () => ({ left: ["", ""], right: ["", ""], key: [] }),
    editForm: (c) => {
      const left = arr(c.left).length ? arr(c.left) : ["", ""];
      const right = arr(c.right).length ? arr(c.right) : ["", ""];
      return `
        <label>Left column, one per line</label><textarea name="left">${esc(left.join("\n"))}</textarea>
        <label>Right column, one per line</label><textarea name="right">${esc(right.join("\n"))}</textarea>
        <p class="hint">The key below lists, for each left row in order, which right row (1, 2, 3…) it matches.</p>
        <label>Key, one number per line (matches the left rows in order)</label>
        <textarea name="key">${esc(arr(c.key).join("\n"))}</textarea>`;
    },
    readForm: (f) => ({
      left: lines((f.querySelector('[name="left"]') as HTMLTextAreaElement).value),
      right: lines((f.querySelector('[name="right"]') as HTMLTextAreaElement).value),
      key: lines((f.querySelector('[name="key"]') as HTMLTextAreaElement).value).map((n) => Number(n) || 0),
    }),
    renderStudent: (c) => {
      const left = arr(c.left);
      const right = arr(c.right);
      if (!left.length || !right.length) return `<p class="hint">Add both columns.</p>`;
      // The paper's own shape: a 3-column table — Words | Definitions | Answers — numbered words,
      // lettered definitions, and a blank the student fills with the matching letter.
      const rowCount = Math.max(left.length, right.length);
      const rows = Array.from({ length: rowCount }, (_, i) => {
        const word = left[i] !== undefined ? `${i + 1}. ${esc(left[i]!)}` : "";
        const def = right[i] !== undefined ? `${String.fromCharCode(65 + i)}) ${esc(right[i]!)}` : "";
        const answer = left[i] !== undefined ? `${i + 1}. ______` : "";
        return `<tr><td>${word}</td><td>${def}</td><td>${answer}</td></tr>`;
      }).join("");
      return `<table class="blk-match-table"><thead><tr><th>Words</th><th>Definitions</th><th>Answers</th></tr></thead><tbody>${rows}</tbody></table>`;
    },
    renderKey: (c) => {
      const key = arr(c.key).map(Number);
      return key.length ? key.map((r, i) => `${i + 1}→${String.fromCharCode(65 + (r - 1))}`).join(" · ") : `<span class="hint">No key set.</span>`;
    },
    summary: (c) => `${arr(c.left).length} pairs`,
  },

  spelling: {
    newContent: () => ({ count: 10, words: [], script: "" }),
    editForm: (c) => `
      <label>Number of blanks</label><input type="number" name="count" min="1" max="30" value="${num(c.count, 10)}" />
      <label>Dictation script (teacher-only — read aloud, never printed for students)</label>
      <textarea name="script" placeholder="Read each word twice, then use it in a sentence.">${esc(str(c.script))}</textarea>
      <label>Answer key, one word per line, in blank order</label>
      <textarea name="words">${esc(arr(c.words).join("\n"))}</textarea>`,
    readForm: (f) => ({
      count: Math.max(1, num(Number((f.querySelector('[name="count"]') as HTMLInputElement).value), 10)),
      script: (f.querySelector('[name="script"]') as HTMLTextAreaElement).value.trim(),
      words: lines((f.querySelector('[name="words"]') as HTMLTextAreaElement).value),
    }),
    renderStudent: (c) => {
      // Two columns (1–5 | 6–10…), the paper's own layout — a numbered blank, nothing else.
      const count = Math.max(1, num(c.count, 10));
      const half = Math.ceil(count / 2);
      const blank = (n: number) => `<div class="blk-spelling-blank"><span>${n}.</span><span class="blk-line"></span></div>`;
      const colA = Array.from({ length: half }, (_, i) => blank(i + 1)).join("");
      const colB = Array.from({ length: count - half }, (_, i) => blank(half + i + 1)).join("");
      return `<div class="blk-spelling"><div class="blk-spelling-col">${colA}</div><div class="blk-spelling-col">${colB}</div></div>`;
    },
    renderKey: (c) => {
      const note = str(c.script) ? `<p class="blk-teacher-note">${esc(str(c.script))}</p>` : "";
      const words = arr(c.words);
      return `${note}${words.length ? words.map((w, i) => `(${i + 1}) ${esc(w)}`).join(" · ") : `<span class="hint">No words listed.</span>`}`;
    },
    summary: (c) => `${num(c.count, 10)} words${arr(c.words).length ? ` · ${arr(c.words).slice(0, 3).join(", ")}${arr(c.words).length > 3 ? "…" : ""}` : ""}`,
  },

  short_answer: {
    newContent: () => ({ question: "", lines: 1, key: "" }),
    editForm: (c) => `
      <label>Question</label><textarea name="question">${esc(str(c.question))}</textarea>
      <label>Writing lines</label><input type="number" name="lines" min="1" max="10" value="${num(c.lines, 1)}" />
      <label>Answer key</label><textarea name="key">${esc(str(c.key))}</textarea>`,
    readForm: (f) => ({
      question: (f.querySelector('[name="question"]') as HTMLTextAreaElement).value.trim(),
      lines: Math.max(1, num(Number((f.querySelector('[name="lines"]') as HTMLInputElement).value), 1)),
      key: (f.querySelector('[name="key"]') as HTMLTextAreaElement).value.trim(),
    }),
    renderStudent: (c) => {
      const q = str(c.question) ? `<p class="blk-stem">${esc(str(c.question))}</p>` : `<p class="hint">Add the question.</p>`;
      const ruleLines = Array.from({ length: Math.max(1, num(c.lines, 1)) }, () => `<div class="blk-line"></div>`).join("");
      return `<div class="blk-short">${q}${ruleLines}</div>`;
    },
    renderKey: (c) => (str(c.key) ? esc(str(c.key)) : `<span class="hint">No answer set.</span>`),
    summary: (c) => str(c.question).slice(0, 80) || "(empty)",
  },

  writing: {
    newContent: () => ({ prompt: "", wordCount: 40, lines: 8 }),
    editForm: (c) => `
      <label>Writing prompt</label><textarea name="prompt">${esc(str(c.prompt))}</textarea>
      <div style="display:flex;gap:8px">
        <div style="flex:1"><label>Target word count</label><input type="number" name="wordCount" min="0" value="${num(c.wordCount, 40)}" /></div>
        <div style="flex:1"><label>Writing lines</label><input type="number" name="lines" min="1" max="30" value="${num(c.lines, 8)}" /></div>
      </div>`,
    readForm: (f) => ({
      prompt: (f.querySelector('[name="prompt"]') as HTMLTextAreaElement).value.trim(),
      wordCount: Math.max(0, num(Number((f.querySelector('[name="wordCount"]') as HTMLInputElement).value), 40)),
      lines: Math.max(1, num(Number((f.querySelector('[name="lines"]') as HTMLInputElement).value), 8)),
    }),
    renderStudent: (c) => {
      const p = str(c.prompt) ? `<p class="blk-stem">${esc(str(c.prompt))}</p>` : `<p class="hint">Add the writing prompt.</p>`;
      const wc = num(c.wordCount, 0) > 0 ? `<p class="blk-hint">About ${num(c.wordCount)} words.</p>` : "";
      const ruleLines = Array.from({ length: Math.max(1, num(c.lines, 8)) }, () => `<div class="blk-line"></div>`).join("");
      return `<div class="blk-writing">${p}${wc}${ruleLines}</div>`;
    },
    renderKey: () => `<span class="hint">Open response — mark by the department's own criteria.</span>`,
    summary: (c) => str(c.prompt).slice(0, 80) || "(empty)",
  },

  listening: {
    newContent: () => ({ trackLabel: "", items: [""], key: [""] }),
    editForm: (c) => `
      <label>Track / script label (for the teacher — what to read or play)</label>
      <input name="trackLabel" value="${esc(str(c.trackLabel))}" placeholder="Read aloud twice, normal speed" />
      <label>Items the student sees, one per line</label>
      <textarea name="items">${esc(arr(c.items).join("\n"))}</textarea>
      <label>Answer key, one per line (matches the items in order)</label>
      <textarea name="key">${esc(arr(c.key).join("\n"))}</textarea>`,
    readForm: (f) => ({
      trackLabel: (f.querySelector('[name="trackLabel"]') as HTMLInputElement).value.trim(),
      items: lines((f.querySelector('[name="items"]') as HTMLTextAreaElement).value),
      key: lines((f.querySelector('[name="key"]') as HTMLTextAreaElement).value),
    }),
    renderStudent: (c) => {
      const items = arr(c.items);
      if (!items.length) return `<p class="hint">Add the items the student will answer.</p>`;
      const rows = items.map((it, i) => `<div class="blk-line-item"><span>${i + 1}. ${esc(it)}</span><span class="blk-line"></span></div>`).join("");
      return `<div class="blk-listening">${rows}</div>`;
    },
    renderKey: (c) => {
      const label = str(c.trackLabel) ? `<p class="blk-teacher-note">${esc(str(c.trackLabel))}</p>` : "";
      const key = arr(c.key);
      return `${label}${key.length ? key.map((k, i) => `(${i + 1}) ${esc(k)}`).join(" · ") : `<span class="hint">No answers listed.</span>`}`;
    },
    summary: (c) => str(c.trackLabel).slice(0, 80) || `${arr(c.items).length} items`,
  },

  picture: {
    newContent: () => ({ imageDataUrl: "", caption: "" }),
    editForm: (c) => `
      <label>Image (JPG/PNG, kept small — about 300KB or less)</label>
      <input type="file" name="image" accept="image/png,image/jpeg" />
      ${c.imageDataUrl ? `<p class="hint">An image is already attached — choose a file only to replace it.</p><img src="${esc(str(c.imageDataUrl))}" style="max-width:160px;display:block;margin:6px 0;border-radius:8px" />` : ""}
      <input type="hidden" name="imageDataUrl" value="${esc(str(c.imageDataUrl))}" />
      <label>Caption (optional)</label><input name="caption" value="${esc(str(c.caption))}" />`,
    readForm: (f) => ({
      imageDataUrl: (f.querySelector('[name="imageDataUrl"]') as HTMLInputElement).value,
      caption: (f.querySelector('[name="caption"]') as HTMLInputElement).value.trim(),
    }),
    renderStudent: (c) => {
      const img = str(c.imageDataUrl) ? `<img src="${esc(str(c.imageDataUrl))}" class="blk-picture-img" />` : `<p class="hint">Add a picture.</p>`;
      const cap = str(c.caption) ? `<p class="blk-caption">${esc(str(c.caption))}</p>` : "";
      return `<div class="blk-picture">${img}${cap}</div>`;
    },
    renderKey: () => "",
    summary: (c) => str(c.caption) || (str(c.imageDataUrl) ? "(picture attached)" : "(no picture yet)"),
  },
};

export function blockDef(type: BlockType): BlockDef {
  return BLOCKS[type];
}

export function newBlock(type: BlockType): EditorBlock {
  return { id: crypto.randomUUID(), type, marks: 0, bloom: null, difficulty: null, content: BLOCKS[type].newContent() };
}

/** ≤300KB, downscaled if needed — used by the picture block's file input (test-editor.ts wires it). */
export async function fileToCappedDataUrl(file: File, maxBytes = 300_000): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  if (dataUrl.length <= maxBytes) return dataUrl;

  // Downscale via canvas until it fits (or we give up shrinking further).
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Could not read that image."));
    el.src = dataUrl;
  });
  let scale = Math.sqrt(maxBytes / dataUrl.length);
  for (let attempt = 0; attempt < 5; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL("image/jpeg", 0.82);
    if (out.length <= maxBytes || attempt === 4) return out;
    scale *= 0.75;
  }
  return dataUrl;
}
